import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import L from "leaflet";
import LoginPopup from "components/login-popup";
import { IconButton, Tooltip } from "@material-ui/core";
import {
  Home as HomeIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CompareArrows as CompareArrowsIcon
} from "@material-ui/icons";
import "leaflet/dist/leaflet.css";
import parseGeoraster from "georaster";
import GeoRasterLayer from "georaster-layer-for-leaflet";
import api from "api";
import { createEsriImageryTileLayer } from "components/maps/esriTileLayer";
import "./GeoTiffViewer.css";

const RASTER_EXT = ["tif", "tiff"];

function getFileExt(file) {
  const candidate = (file?.name || file?.path || file?.url || "").toString().split("?")[0].toLowerCase();
  return candidate.split(".").pop() || "";
}

function isRaster(ext) {
  return RASTER_EXT.includes(ext);
}

/** Mesmo mapeamento de cores do GeoTiffViewer (com suporte a mode) */
function createPixelValuesToColorFn(georaster, mode = "auto") {
  const noData = georaster.noDataValue;
  const bands = georaster.numberOfRasters || 1;
  const mins = georaster.mins;
  const ranges = georaster.ranges;
  return function pixelValuesToColorFn(values) {
    const hasData = values.every(
      v => v !== undefined && v !== null && (noData === undefined || v !== noData)
    );
    if (!hasData) return undefined;
    const normScalar = rawVal => {
      let v = Number(rawVal);
      if (mins && ranges && ranges[0] > 0) {
        v = (v - mins[0]) / ranges[0];
      } else {
        if (v > 1) v = v <= 255 ? v / 255 : v / 65535;
      }
      v = Math.max(0, Math.min(1, v));
      return Math.round(v * 255);
    };
    const toGrayFromBandIndex = bandIndex => {
      const raw = Number(values[bandIndex]);
      if (raw >= 254 || raw === 65535) return undefined;
      const c = normScalar(raw);
      return `rgb(${c},${c},${c})`;
    };
    if (bands === 1 || mode.startsWith("band")) {
      const idx = mode.startsWith("band") && !Number.isNaN(Number(mode.replace("band", "")))
        ? Math.max(0, Math.min(bands - 1, Number(mode.replace("band", "")) - 1))
        : 0;
      return toGrayFromBandIndex(idx);
    }
    if (bands >= 3) {
      const rIdx = 0, gIdx = 1, bIdx = 2;
      if (mode === "r") return toGrayFromBandIndex(rIdx);
      if (mode === "g") return toGrayFromBandIndex(gIdx);
      if (mode === "b") return toGrayFromBandIndex(bIdx);
      const r = normScalar(values[rIdx]);
      const g = normScalar(values[gIdx]);
      const b = normScalar(values[bIdx]);
      if (r >= 254 && g >= 254 && b >= 254) return undefined;
      if (bands >= 4) {
        const a = Math.max(0, Math.min(1, Number(values[3]) / 255));
        return `rgba(${r},${g},${b},${a})`;
      }
      return `rgb(${r},${g},${b})`;
    }
    return undefined;
  };
}

/**
 * Viewer de GeoTIFF compartilhado via link público (?token=...).
 * Usa proxy /projects/shared/{token}/blob para evitar CORS.
 */
const SharedGeoTiff = () => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const layerARef = useRef(null);
  const layerBRef = useRef(null);
  const georasterCacheRef = useRef(new Map());
  const activeGeorasterRef = useRef(null);
  const compareWrapRef = useRef(null);
  const MAX_GEORASTER_CACHE = 3;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIndexA, setCompareIndexA] = useState(0);
  const [compareIndexB, setCompareIndexB] = useState(1);
  const [splitPercent, setSplitPercent] = useState(50);
  const [splitDragging, setSplitDragging] = useState(false);
  const [rasterLoading, setRasterLoading] = useState(false);

  const rasterFiles = files.filter(f => isRaster(getFileExt(f)));
  const canCompare = rasterFiles.length >= 2;

  useEffect(() => {
    if (!token) {
      setError("Link inválido ou token ausente.");
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const loadSharedFiles = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/projects/shared/${token}`);
        const data = response.data || {};
        const rawFiles = data.files || data || [];
        const list = Array.isArray(rawFiles) ? rawFiles : rawFiles.files || [];
        const tifFiles = list.filter(f => {
          const url = (f.url || "").toString();
          const nameLower = url.split("?")[0].toLowerCase();
          return nameLower.endsWith(".tif") || nameLower.endsWith(".tiff");
        });
        if (!tifFiles.length) {
          setError("Nenhum arquivo de GeoView compartilhado foi encontrado para este link.");
        }
        setFiles(tifFiles);
      } catch (err) {
        console.error("Erro ao carregar GeoView compartilhado:", err);
        setError("Erro ao carregar o GeoView compartilhado. Verifique se o link ainda é válido.");
      } finally {
        setLoading(false);
      }
    };
    loadSharedFiles();
  }, [token]);

  useEffect(() => {
    if (canCompare && rasterFiles.length > 0) {
      setCompareIndexA(prev => Math.min(prev, rasterFiles.length - 1));
      setCompareIndexB(prev => {
        const next = Math.min(prev, rasterFiles.length - 1);
        return next;
      });
    }
  }, [canCompare, rasterFiles.length]);

  useEffect(() => {
    if (compareMode && canCompare && compareIndexA === compareIndexB) {
      setCompareIndexB((compareIndexA + 1) % rasterFiles.length);
    }
  }, [compareMode, canCompare, compareIndexA, compareIndexB, rasterFiles.length]);

  const fetchGeoraster = useCallback(async (file, forceFullDownload = false) => {
    const cacheKey = file.path || file.url;
    const cached = georasterCacheRef.current.get(cacheKey);
    if (cached && !forceFullDownload) return cached;

    const baseUrl = (api.defaults.baseURL || "").replace(/\/$/, "");
    const proxyUrl = file.path
      ? `${baseUrl}/projects/shared/${token}/blob?path=${encodeURIComponent(file.path)}`
      : file.url;

    let georaster;
    if (forceFullDownload && file.path) {
      const res = await api.get(
        `/projects/shared/${token}/blob?path=${encodeURIComponent(file.path)}`,
        { responseType: "arraybuffer" }
      );
      georaster = await parseGeoraster(res.data);
    } else if (forceFullDownload && file.url) {
      const res = await fetch(file.url, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      georaster = await parseGeoraster(await res.arrayBuffer());
    } else {
      try {
        georaster = await parseGeoraster(proxyUrl);
      } catch (urlErr) {
        if (!file.path) throw urlErr;
        const res = await api.get(
          `/projects/shared/${token}/blob?path=${encodeURIComponent(file.path)}`,
          { responseType: "arraybuffer" }
        );
        georaster = await parseGeoraster(res.data);
      }
    }

    if (!forceFullDownload) {
      if (georasterCacheRef.current.size >= MAX_GEORASTER_CACHE) {
        const firstKey = georasterCacheRef.current.keys().next().value;
        georasterCacheRef.current.delete(firstKey);
      }
      georasterCacheRef.current.set(cacheKey, georaster);
    }
    return georaster;
  }, [token]);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    const map = L.map(mapContainerRef.current, {
      center: [0, 0],
      zoom: 2,
      zoomControl: false,
      maxZoom: 28
    });
    L.control.zoom({ position: "topright" }).addTo(map);
    createEsriImageryTileLayer({ maxZoom: 28 }).addTo(map);
    mapRef.current = map;
    return () => {
      if (layerRef.current && mapRef.current) {
        mapRef.current.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      if (layerARef.current && mapRef.current) {
        mapRef.current.removeLayer(layerARef.current);
        layerARef.current = null;
      }
      if (layerBRef.current && mapRef.current) {
        mapRef.current.removeLayer(layerBRef.current);
        layerBRef.current = null;
      }
      georasterCacheRef.current.clear();
      activeGeorasterRef.current = null;
      mapRef.current.remove();
      mapRef.current = null;
    };
  }, []);

  const handleHome = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    if (compareMode && layerARef.current && layerBRef.current) {
      const bA = layerARef.current.getBounds?.();
      const bB = layerBRef.current.getBounds?.();
      const bounds = bA && bB ? bA.extend(bB) : bA || bB;
      if (bounds && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 22 });
      }
    } else if (layerRef.current && layerRef.current.getBounds?.().isValid()) {
      map.fitBounds(layerRef.current.getBounds(), { padding: [40, 40], maxZoom: 22 });
    }
  }, [compareMode]);

  const handleZoomIn = useCallback(() => {
    if (mapRef.current) mapRef.current.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) mapRef.current.zoomOut();
  }, []);

  const handleSplitMouseDown = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setSplitDragging(true);
  }, []);

  useEffect(() => {
    if (!splitDragging) return;
    const onMove = e => {
      const wrap = compareWrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const percent = Math.max(15, Math.min(85, ((e.clientX - rect.left) / rect.width) * 100));
      setSplitPercent(percent);
    };
    const onUp = () => setSplitDragging(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [splitDragging]);

  useEffect(() => {
    if (!compareMode || !mapRef.current || !token || rasterFiles.length < 2) return;

    let idxA = Math.min(compareIndexA, rasterFiles.length - 1);
    let idxB = Math.min(compareIndexB, rasterFiles.length - 1);
    if (idxA === idxB) {
      idxB = (idxA + 1) % rasterFiles.length;
    }

    const fileA = rasterFiles[idxA];
    const fileB = rasterFiles[idxB];
    if (!fileA || !fileB || fileA === fileB) return;

    const map = mapRef.current;
    let cancelled = false;

    (async () => {
      try {
        setRasterLoading(true);
        setError(null);

        if (layerRef.current && map) {
          map.removeLayer(layerRef.current);
          layerRef.current = null;
        }

        const georasterA = await fetchGeoraster(fileA, true);
        if (cancelled) return;

        const georasterB = await fetchGeoraster(fileB, true);
        if (cancelled) return;

        if (layerARef.current && map) {
          map.removeLayer(layerARef.current);
          layerARef.current = null;
        }
        if (layerBRef.current && map) {
          map.removeLayer(layerBRef.current);
          layerBRef.current = null;
        }

        const compareResolution = 64;
        const newA = new GeoRasterLayer({
          georaster: georasterA,
          opacity: 1,
          resolution: compareResolution,
          keepBuffer: 8,
          pixelValuesToColorFn: createPixelValuesToColorFn(georasterA)
        });
        newA.addTo(map);
        layerARef.current = newA;

        const newB = new GeoRasterLayer({
          georaster: georasterB,
          opacity: 0,
          resolution: compareResolution,
          keepBuffer: 8,
          pixelValuesToColorFn: createPixelValuesToColorFn(georasterB)
        });
        newB.addTo(map);
        layerBRef.current = newB;

        map.invalidateSize();
        const bA = newA.getBounds?.();
        const bB = newB.getBounds?.();
        const bounds = bA && bB ? bA.extend(bB) : bA || bB;
        if (bounds && bounds.isValid()) {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 22 });
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Erro ao carregar GeoView para comparação:", err);
          setError("Não foi possível carregar os arquivos de GeoView para comparação.");
        }
      } finally {
        if (!cancelled) setRasterLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (layerARef.current && map) {
        map.removeLayer(layerARef.current);
        layerARef.current = null;
      }
      if (layerBRef.current && map) {
        map.removeLayer(layerBRef.current);
        layerBRef.current = null;
      }
      setRasterLoading(false);
    };
  }, [compareMode, compareIndexA, compareIndexB, token, rasterFiles, files, activeIndex, fetchGeoraster]);

  useEffect(() => {
    if (!compareMode || !layerBRef.current) return;
    if (typeof layerBRef.current.setOpacity === "function") {
      layerBRef.current.setOpacity(splitPercent / 100);
    }
  }, [compareMode, splitPercent]);

  useEffect(() => {
    if (compareMode || !mapRef.current || !files.length || activeIndex >= files.length || !token) return;

    const file = files[activeIndex];
    if (!isRaster(getFileExt(file))) return;

    const loadGeoTiff = async () => {
      try {
        setRasterLoading(true);
        setError(null);

        const georaster = await fetchGeoraster(file);
        activeGeorasterRef.current = georaster;

        if (layerRef.current && mapRef.current) {
          mapRef.current.removeLayer(layerRef.current);
          layerRef.current = null;
        }

        const layer = new GeoRasterLayer({
          georaster,
          opacity,
          resolution: 128,
          pixelValuesToColorFn: createPixelValuesToColorFn(georaster)
        });
        layer.addTo(mapRef.current);
        layerRef.current = layer;

        mapRef.current.invalidateSize();
        const bounds = layer.getBounds();
        if (bounds && bounds.isValid()) {
          mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 22 });
        }
      } catch (err) {
        console.error("Erro ao carregar GeoView compartilhado:", err);
        setError("Não foi possível carregar o arquivo de GeoView compartilhado.");
      } finally {
        setRasterLoading(false);
      }
    };

    loadGeoTiff();
  }, [files, activeIndex, opacity, compareMode, token, fetchGeoraster]);

  useEffect(() => {
    if (layerRef.current && layerRef.current.setOpacity) {
      layerRef.current.setOpacity(opacity);
    }
  }, [opacity]);

  useEffect(() => {
    if ((rasterFiles.length > 0 || compareMode) && mapRef.current) {
      setTimeout(() => {
        if (mapRef.current) mapRef.current.invalidateSize();
      }, 50);
    }
  }, [compareMode, activeIndex, rasterFiles.length]);

  useEffect(() => {
    document.body.style.setProperty("overflow", "hidden", "important");
    return () => document.body.style.setProperty("overflow", "auto", "important");
  }, []);

  if (error && !loading && !token) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 p-4">
        <p className="text-muted">{error}</p>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        top: "0",
        left: "0",
        right: "0",
        bottom: "0",
        overflow: "hidden",
        zIndex: 1
      }}
    >
      <div className="geotiff-viewer">
        <header className="geotiff-viewer__header">
          <div className="geotiff-viewer__header-left">
            <div>
              <h1 className="geotiff-viewer__title">GeoView compartilhado</h1>
              <p className="geotiff-viewer__date">
                Este link permite visualizar arquivos de GeoView (GeoTIFF) compartilhados.
              </p>
            </div>
          </div>
          <div className="geotiff-viewer__controls">
            {files.length > 1 && !compareMode && (
              <div className="geotiff-viewer__select-wrap">
                <label htmlFor="geotiff-shared-select">Arquivo:</label>
                <select
                  id="geotiff-shared-select"
                  value={activeIndex}
                  onChange={e => setActiveIndex(Number(e.target.value))}
                  className="geotiff-viewer__select"
                >
                  {files.map((f, i) => (
                    <option key={i} value={i}>
                      {f.name || (f.url && f.url.split("/").pop()) || `Arquivo ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {compareMode && canCompare && (
              <>
                <div className="geotiff-viewer__select-wrap">
                  <label htmlFor="compare-shared-a">Momento A:</label>
                  <select
                    id="compare-shared-a"
                    value={Math.min(compareIndexA, rasterFiles.length - 1)}
                    onChange={e => {
                      const v = Number(e.target.value);
                      setCompareIndexA(v);
                      if (v === compareIndexB && rasterFiles.length > 1) {
                        setCompareIndexB((v + 1) % rasterFiles.length);
                      }
                    }}
                    className="geotiff-viewer__select"
                  >
                    {rasterFiles.map((f, i) => (
                      <option key={i} value={i}>
                        {f.name || (f.url && f.url.split("/").pop()) || `Arquivo ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="geotiff-viewer__select-wrap">
                  <label htmlFor="compare-shared-b">Momento B:</label>
                  <select
                    id="compare-shared-b"
                    value={Math.min(compareIndexB, rasterFiles.length - 1)}
                    onChange={e => {
                      const v = Number(e.target.value);
                      setCompareIndexB(v);
                      if (v === compareIndexA && rasterFiles.length > 1) {
                        setCompareIndexA((v + 1) % rasterFiles.length);
                      }
                    }}
                    className="geotiff-viewer__select"
                  >
                    {rasterFiles.map((f, i) => (
                      <option key={i} value={i}>
                        {f.name || (f.url && f.url.split("/").pop()) || `Arquivo ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {files.length > 0 && (
              <div className="geotiff-viewer__opacity-wrap">
                <label htmlFor="geotiff-shared-opacity">Opacidade:</label>
                <input
                  id="geotiff-shared-opacity"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={opacity}
                  onChange={e => setOpacity(Number(e.target.value))}
                  className="geotiff-viewer__opacity"
                />
              </div>
            )}
          </div>
        </header>

        <div className="geotiff-viewer__map-wrap" ref={compareWrapRef}>
          <div ref={mapContainerRef} className="geotiff-viewer__map-container" />

          {compareMode && canCompare && (
            <>
              <div
                className="geotiff-viewer__compare-overlay-b"
                style={{ left: `${splitPercent}%` }}
                aria-hidden="true"
              />
              <div
                className={`geotiff-viewer__compare-divider ${splitDragging ? "geotiff-viewer__compare-divider--dragging" : ""}`}
                style={{ left: `${splitPercent}%` }}
                onMouseDown={handleSplitMouseDown}
                role="separator"
                aria-label="Arrastar para alternar entre os dois TIFFs"
              >
                <span className="geotiff-viewer__compare-divider-handle">⋮</span>
              </div>
            </>
          )}

          {(rasterFiles.length > 0 || compareMode) && (
            <div className="geotiff-viewer__toolbar">
              <Tooltip title="Centralizar na imagem (HOME)">
                <IconButton onClick={handleHome} size="small" className="geotiff-viewer__toolbar-btn" aria-label="HOME">
                  <HomeIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Aumentar zoom">
                <IconButton onClick={handleZoomIn} size="small" className="geotiff-viewer__toolbar-btn" aria-label="Zoom in">
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Diminuir zoom">
                <IconButton onClick={handleZoomOut} size="small" className="geotiff-viewer__toolbar-btn" aria-label="Zoom out">
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={canCompare ? "Comparar dois TIFFs" : "É necessário pelo menos 2 arquivos TIFF para comparar"}>
                <span>
                  <IconButton
                    onClick={() => {
                      if (!compareMode && canCompare) {
                        const currentRasterIdx = rasterFiles.findIndex(
                          rf => (rf.path || rf.url) === (files[activeIndex]?.path || files[activeIndex]?.url)
                        );
                        if (currentRasterIdx >= 0) {
                          setCompareIndexA(currentRasterIdx);
                          setCompareIndexB(currentRasterIdx === 0 ? 1 : 0);
                        }
                      } else if (compareMode) {
                        setError(null);
                      }
                      setCompareMode(!compareMode);
                    }}
                    disabled={!canCompare}
                    size="small"
                    className={`geotiff-viewer__toolbar-btn ${compareMode ? "geotiff-viewer__toolbar-btn--active" : ""}`}
                    aria-label="Comparar"
                  >
                    <CompareArrowsIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          )}

          {loading && (
            <div className="geotiff-viewer__loading">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Carregando...</span>
              </div>
              <p>Carregando GeoView compartilhado...</p>
            </div>
          )}
          {rasterLoading && (
            <div className="geotiff-viewer__loading">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Carregando...</span>
              </div>
              <p>{compareMode ? "Carregando arquivos para comparação (pode demorar)..." : "Processando TIFF... Aguarde."}</p>
            </div>
          )}
          {error && !loading && !rasterLoading && (
            <div className="geotiff-viewer__error">
              <p>{error}</p>
            </div>
          )}
        </div>
      </div>
      <LoginPopup isPublic={true} />
    </div>
  );
};

export default SharedGeoTiff;
